import { type Team, type User } from '@/api/model'
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

// STORAGE_VERSION is used to expire legacy versioned data when there's some kind of breaking change that we want to make. Just increment this version number and all the legacy data will get expired when the user loads the app next time.
const STORAGE_VERSION = 7

// Custom storage implementation with versioning such that we can expire legacy versioned data when there's some kind of breaking change that we want to make.
export const versionedStorage = {
  getItem: (key: string, initialValue: unknown) => {
    try {
      const stored = localStorage.getItem(key)
      if (!stored) return initialValue

      const parsed = JSON.parse(stored)
      if (parsed.version !== STORAGE_VERSION) {
        // Version mismatch - clear old data
        localStorage.removeItem(key)
        return initialValue
      }
      return parsed.data
    } catch {
      return initialValue
    }
  },
  setItem: (key: string, value: any) => {
    const versionedData = {
      version: STORAGE_VERSION,
      data: value,
    }
    localStorage.setItem(key, JSON.stringify(versionedData))
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key)
  },
}

export const tokenAtom = atomWithStorage<string | null>(
  'tower_token',
  null, // initial nulle
  versionedStorage, // storage
  { getOnInit: true }, // options
) // undefined means loading...

export const refreshTokenAtom = atomWithStorage<string | null>(
  'tower_refresh_token',
  null,
  versionedStorage,
  { getOnInit: true },
)

export const userAtom = atomWithStorage<User | null>('tower_user', null, versionedStorage, {
  getOnInit: true,
}) // undefined means loading...

export const teamsAtom = atomWithStorage<Team[] | null>('tower_teams', null, versionedStorage, {
  getOnInit: true,
})

export const personalAccountAtom = atom((get) => {
  const teams = get(teamsAtom)

  if (!teams) return null

  const personalAccount = teams.find((team) => team.type === 'personal')

  return personalAccount
})

export const terminalHeightAtom = atomWithStorage<number>(
  'tower_terminal_height',
  400,
  versionedStorage,
  { getOnInit: true },
)

export const DEFAULT_TERMINAL_FONT_SIZE = 0.85

export const terminalFontSizeAtom = atomWithStorage<number>(
  'tower_terminal_font_size',
  DEFAULT_TERMINAL_FONT_SIZE,
  versionedStorage,
  { getOnInit: true },
)

// Terminal Themes
export const TerminalTheme = {
  TomorrowNight: 'tomorrow-night',
  Dracula: 'dracula',
  SolarizedLight: 'solarized-light',
  PaperLight: 'paper-light',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TerminalTheme = (typeof TerminalTheme)[keyof typeof TerminalTheme]

export const DEFAULT_TERMINAL_THEME: TerminalTheme = 'tomorrow-night'

export const terminalThemeAtom = atomWithStorage<TerminalTheme>(
  'tower_terminal_theme',
  DEFAULT_TERMINAL_THEME,
  versionedStorage,
  { getOnInit: true },
)

const getAdminTeamName = () => {
  return import.meta.env.VITE_ADMIN_TEAM_NAME || import.meta.env.VITE_ADMIN_TEAM_SLUG
}

export const isAdminAtom = atom((get) => {
  const user = get(userAtom)
  const teams = get(teamsAtom)

  if (!user || !teams) return false

  const adminTeam = teams.find((team) => team.name === getAdminTeamName())

  return Boolean(adminTeam)
})

type Impersonator = {
  token: string | null
  user: User | null
  teams: Team[] | null
  returnTo?: string | null
}

export const impersonatorAtom = atomWithStorage<Impersonator | null>(
  'tower_impersonator',
  null,
  versionedStorage,
  { getOnInit: true },
)

export const isImpersonatingAdminAtom = atom((get) => {
  const impersonator = get(impersonatorAtom)

  if (!impersonator || !impersonator.teams) return false

  const impersonatingAdminTeam = impersonator.teams.find((team) => team.name === getAdminTeamName())

  return Boolean(impersonatingAdminTeam)
})

export const alertPopupAtom = atomWithStorage<boolean>('alert_popup', true, versionedStorage, {
  getOnInit: true,
})
